/**
 * Notification Context
 * 
 * Provides toast notification functionality throughout the application.
 * Supports success, error, warning, and info notifications using DFX5 design system.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

type AlertColor = 'success' | 'error' | 'warning' | 'info';

export interface NotificationOptions {
  message: string;
  severity?: AlertColor;
  duration?: number;
}

export interface NotificationContextType {
  showNotification: (options: NotificationOptions) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: React.ReactNode;
}

const SEVERITY_STYLES: Record<AlertColor, { bg: string; border: string; text: string; icon: string }> = {
  success: { bg: 'bg-[var(--green-100)]', border: 'border-[var(--green-500)]', text: 'text-[var(--green-900)]', icon: '✓' },
  error:   { bg: 'bg-[#fee2e2]', border: 'border-[var(--destructive)]', text: 'text-[#991b1b]', icon: '✕' },
  warning: { bg: 'bg-[var(--gold-100)]', border: 'border-[var(--gold-500)]', text: 'text-[var(--gold-900)]', icon: '⚠' },
  info:    { bg: 'bg-[var(--primary-50)]', border: 'border-[var(--primary-200)]', text: 'text-[var(--primary-900)]', icon: 'ℹ' },
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('info');
  const [duration, setDuration] = useState(6000);

  const showNotification = useCallback((options: NotificationOptions) => {
    setMessage(options.message);
    setSeverity(options.severity || 'info');
    setDuration(options.duration || 6000);
    setOpen(true);
  }, []);

  const showSuccess = useCallback((msg: string) => showNotification({ message: msg, severity: 'success' }), [showNotification]);
  const showError = useCallback((msg: string) => showNotification({ message: msg, severity: 'error', duration: 8000 }), [showNotification]);
  const showWarning = useCallback((msg: string) => showNotification({ message: msg, severity: 'warning' }), [showNotification]);
  const showInfo = useCallback((msg: string) => showNotification({ message: msg, severity: 'info' }), [showNotification]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => setOpen(false), duration);
    return () => clearTimeout(timer);
  }, [open, duration]);

  const styles = SEVERITY_STYLES[severity];

  return (
    <NotificationContext.Provider value={{ showNotification, showSuccess, showError, showWarning, showInfo }}>
      {children}
      {/* Toast notification */}
      {open && (
        <div className="fixed top-4 right-4 z-[9999] animate-in slide-in-from-top-2 fade-in duration-200">
          <div
            className={`flex items-center gap-3 px-4 py-3 rounded-[var(--radius-md)] border-l-[3px] shadow-lg min-w-[300px] max-w-[480px] ${styles.bg} ${styles.border} ${styles.text}`}
            role="alert"
          >
            <span className="text-base font-bold flex-shrink-0">{styles.icon}</span>
            <span className="text-sm flex-1">{message}</span>
            <button
              onClick={() => setOpen(false)}
              className={`flex-shrink-0 ml-2 opacity-70 hover:opacity-100 transition-opacity ${styles.text}`}
              aria-label="Close notification"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
